import { useState, useCallback, useEffect } from 'react';
import { useZxing } from 'react-zxing';
import { useTranslation } from 'react-i18next';
import axiosInstance from '../utils/axiosConfig';
import { foodbookInstance } from '../utils/axiosConfig';
import { useNavigate, useParams } from 'react-router-dom';
import { Toaster } from "../Components/ui/toaster"
import { useToast } from "@/hooks/use-toast"
import logo from '../images/pslogo.png';

const Scanner = () => {
    const { t } = useTranslation();
    const { toast } = useToast();
    const [isActive, setIsActive] = useState(true);
    const navigate = useNavigate();
    const [result, setResult] = useState('');
    const [productId, setProductId] = useState(null);
    const [productName, setProductName] = useState('');
    const { code } = useParams();
    const [validationError, setValidationError] = useState(false); 

    useEffect(() => {
        let timeoutId;

        axiosInstance.get(`/product/ValidateGuid/${code}`)
            .then(response => {
                if (!response.data) {
                    stop();
                    setValidationError(true);
                }
            })
            .catch(() => { stop(); setValidationError(true); });

        const handleVisibilityChange = () => {
            if (document.hidden) {
                timeoutId = setTimeout(() => {
                    axiosInstance.put(`/product/InvalidateGuid/${code}`).catch(console.error);
                    setIsActive(false);
                    stop();
                }, 5000);
            } else {
                if (timeoutId) clearTimeout(timeoutId);
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            if (timeoutId) clearTimeout(timeoutId);
        };
    }, [code]);

    const playBeep = useCallback(() => {
        const audioContext = new (window.AudioContext || window.webkitAudioContext)();
        const oscillator = audioContext.createOscillator();
        const gainNode = audioContext.createGain();

        oscillator.connect(gainNode);
        gainNode.connect(audioContext.destination);

        oscillator.frequency.value = 1000;
        gainNode.gain.value = 0.25;

        oscillator.start();
        gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.25);
        setTimeout(() => oscillator.stop(), 200);
    }, []);

    const handleSearch = async (ean) => {
        try { 
            const response = await foodbookInstance.get(`/MenuApp/SearchByEan/${ean}`);
            const data = response.data;

            if (data.products && data.products.length > 0) { 
                const foundProductId = data.products[0].id; 
                setProductId(foundProductId);
                setProductName(data.products[0].name);

                await axiosInstance.post(`/product/AddProduct/${code}`, {
                    refid: foundProductId,
                    name: data.products[0].name
                });

                toast({
                    title: t('scanner.productAdded'),
                    description: `${data.products[0].name} ${t('scanner.productAddedDesc')}`,
                    duration: 3000
                });
            } else {
                setProductId(0);
                setProductName(t('scanner.noProductInDatabase'));
            }
        } catch (error) {
            console.error('Error:', error);
            toast({
                title: t('common.error'),
                description: t('scanner.errorAddingProduct'),
                variant: "destructive",
                duration: 3000
            });
        }
    };

    const { ref, stop } = useZxing({
        onDecodeResult(result) {
            setResult(result.getText());
            playBeep();
            handleSearch(result.getText());
        },
        onError(error) { 
            toast({
                title: t('scanner.scannerError'),
                description: error.name === 'NotAllowedError'
                    ? t('scanner.cameraPermissionDenied')
                    : t('scanner.scannerProblem'),
                variant: "destructive",
                duration: 5000
            });
        },
        constraints: {
            video: {
                facingMode: "environment",
                width: { min: 640, ideal: 1280, max: 1920 },
                height: { min: 480, ideal: 720, max: 1080 }
            }
        },
        timeBetweenScans: 500,
        refreshInterval: 100
    });

    if (validationError) {
        return (
            <div className="fixed inset-0 bg-gray-900/90 flex items-center justify-center z-50">
                <div className="bg-white p-6 rounded-lg max-w-sm text-center">
                    <h2 className="text-xl font-bold mb-4">{t('scanner.scannerInactive')}</h2>
                    <p className="mb-4">{t('scanner.scanQRAgain')}</p>
                </div>
            </div>
        );
    }

    if (!isActive) {
        return (
            <div className="fixed inset-0 bg-gray-900/90 flex items-center justify-center z-50">
                <div className="bg-white p-6 rounded-lg max-w-sm text-center">
                    <h2 className="text-xl font-bold mb-4">{t('scanner.noLongerActive')}</h2>
                    <p className="mb-4">{t('scanner.scanAgain')}</p>
                </div>
            </div>
        );
    }

    return (
        <div className="relative w-full max-w-md mx-auto">
            <div className="flex items-center gap-4 mb-6 mt-6 ml-4">
                <img src={logo} className="h-[80px]" />
                <h1 className="text-xl font-normal font-inter text-ps-blue-dark">
                    PS IN FOODSERVICE<br />{t('scanner.title')}
                </h1>
            </div>
            <video ref={ref} className="w-full" />
            <div className="absolute top-12 left-0 w-full h-full flex items-center justify-center">
                <div className="relative w-4/5 h-48">
                    <div className="absolute top-1/2 left-0 w-full h-1 -translate-y-1/2"
                        style={{
                            background: 'linear-gradient(to bottom, transparent 0%, rgba(239, 68, 68, 0.8) 50%, rgba(239, 68, 68, 0.8) 60%, transparent 100%)'
                        }}
                    />
                    <div className="absolute top-0 left-0 w-8 h-8 border-t-2 border-l-2 border-red-500"></div>
                    <div className="absolute top-0 right-0 w-8 h-8 border-t-2 border-r-2 border-red-500"></div>
                    <div className="absolute bottom-0 left-0 w-8 h-8 border-b-2 border-l-2 border-red-500"></div>
                    <div className="absolute bottom-0 right-0 w-8 h-8 border-b-2 border-r-2 border-red-500"></div>
                </div>
            </div>
            <div className="mt-4 text-center space-y-2">
                {result && <p>{t('scanner.scannedCode')}: {result}</p>}
                {productName && <p>{t('scanner.product')}: {productName}</p>}
            </div>
            <Toaster />
        </div>
    );
};

export default Scanner;